import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { useGetApiCompaniesCurrentServicemodulesAbsenceServiceModuleIdAbsencesCompany } from '@/api/generated/company-management-user/company-management-user'
import { AbsenceApproval } from '@/api/model'
import type { AbsenceModel, ServiceModuleModel } from '@/api/model'
import { MonthCalendar, type DayEvent } from '../../calendar/components/MonthCalendar'
import { buildMonthGrid, dayKey } from '../../calendar/components/monthGrid'
import { getAbsenceReasonColor, getAbsenceReasonLabel } from './absenceLabels'

interface Props {
  module: ServiceModuleModel
  /** Optional filter: only absences of this employee (otherwise all). */
  employeeUserId?: string
}

/** Unwrap the query response from the axios wrapper if needed (hooks return the full response). */
function unwrap<T>(data: unknown): T | undefined {
  return ((data as { data?: T })?.data ?? data) as T | undefined
}

/**
 * Month calendar of the **approved** absences. Loads the (non-paginated)
 * company absences in the visible month grid, colors them per absence reason
 * and shows a legend. Ported from the old portal's `AbsenceCalendar`, but onto
 * React Query + the existing `MonthCalendar` instead of react-big-calendar/MUI.
 */
export function AbsenceCalendar({ module, employeeUserId }: Props) {
  const { t } = useTranslation('service-modules-absence')
  const id = module.id
  const [month, setMonth] = useState(() => dayjs().startOf('month'))
  const range = useMemo(() => buildMonthGrid(month), [month])

  const absencesQuery = useGetApiCompaniesCurrentServicemodulesAbsenceServiceModuleIdAbsencesCompany(id, {
    employeeUserId,
    startDate: range.gridStart.format('YYYY-MM-DD'),
    endDate: range.gridEnd.format('YYYY-MM-DD'),
  })

  // Only approved absences appear in the calendar.
  const approved = useMemo(
    () =>
      (unwrap<AbsenceModel[]>(absencesQuery.data) ?? []).filter(
        (a) => a.absenceApproval === AbsenceApproval.Approved,
      ),
    [absencesQuery.data],
  )

  // Legend: one entry with color + label per absence reason that occurs.
  const legend = useMemo(() => {
    const seen = new Map<string, string>()
    for (const absence of approved) {
      const reason = absence.absenceReason
      if (reason && !seen.has(reason)) seen.set(reason, getAbsenceReasonLabel(t, reason))
    }
    return Array.from(seen.entries()).map(([reason, label]) => ({
      id: reason,
      label,
      color: getAbsenceReasonColor(reason),
    }))
  }, [approved, t])

  // Distribute events across their days (multi-day absences in each cell).
  const eventsByDay = useMemo(() => {
    const map = new Map<string, DayEvent[]>()
    for (const absence of approved) {
      const reason = getAbsenceReasonLabel(t, absence.absenceReason)
      const label = `${absence.employeeName || '—'} · ${reason}`
      const color = getAbsenceReasonColor(absence.absenceReason)
      const last = dayjs(absence.endTimestamp).startOf('day')
      let day = dayjs(absence.startTimestamp).startOf('day')
      let guard = 0
      while ((day.isBefore(last) || day.isSame(last, 'day')) && guard < 366) {
        const key = dayKey(day)
        const list = map.get(key) ?? []
        list.push({ key: `${absence.id}-${key}`, label, color })
        map.set(key, list)
        day = day.add(1, 'day')
        guard++
      }
    }
    return map
  }, [approved, t])

  return (
    <div className="flex flex-col gap-3">
      {legend.length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1.5">
          {legend.map((entry) => (
            <div key={entry.id} className="flex items-center gap-1.5">
              <span className="size-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
              <span className="text-sm text-muted-foreground">{entry.label}</span>
            </div>
          ))}
        </div>
      )}

      <MonthCalendar
        month={month}
        eventsByDay={eventsByDay}
        isLoading={absencesQuery.isLoading || absencesQuery.isFetching}
        onPrev={() => setMonth((m) => m.subtract(1, 'month'))}
        onNext={() => setMonth((m) => m.add(1, 'month'))}
        onToday={() => setMonth(dayjs().startOf('month'))}
      />
    </div>
  )
}
